// src/components/Header.jsx
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Key, LogOut, ArrowLeft, X, Eye, EyeOff } from "lucide-react";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signInWithEmailAndPassword,
} from "firebase/auth";

import { firebaseConfig } from "shared/lib/config";
import logo from "assets/images/potentialogo.png";


/** Guarded Firebase init (works even if Login hasn't mounted yet) */
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);

export default function Header({
  profile,
  onLogout,
  schoolName,
  title = "Laporan Asesmen Kelas",
  showBack = false,
  onBack,
}) {
  const [open, setOpen] = useState(false);          
  const [openSheet, setOpenSheet] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const ddRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!ddRef.current) return;
      if (!ddRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const initials = (() => {
    const src = (profile?.name || profile?.email || "?").trim();
    const parts = src.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return src.slice(0, 2).toUpperCase();
  })();

  const resolvedSchool =
    schoolName ||
    profile?.schoolId?.name ||
    profile?.school?.name ||
    profile?.schoolName ||
    "";

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");

    if (!currentPw?.trim() || !newPw?.trim() || !confirmPw?.trim()) {
      setErr("Semua kolom kata sandi harus diisi. Tidak boleh hanya spasi.");
      return;
    }
    if (newPw.trim().length < 6) {
      setErr("Kata sandi setidaknya harus 6 karakter.");
      return;
    }
    if (newPw !== confirmPw) {
      setErr("Kata sandi tidak sesuai.");
      return;
    }
    if (currentPw && newPw === currentPw) {
      setErr("Kata sandi baru tidak boleh sama dengan kata sandi saat ini.");
      return;
    }

    try {
      setSubmitting(true);
      let user = auth.currentUser;

      if (!user) {
        const email = profile?.email;
        if (!email) throw new Error("Tidak ada pengguna yang terotentikasi. Harap masuk kembali.");
        const signInCred = await signInWithEmailAndPassword(auth, email, currentPw);
        user = signInCred.user;
      } else {
        const cred = EmailAuthProvider.credential(user.email, currentPw);
        await reauthenticateWithCredential(user, cred);
      }

      await updatePassword(user, newPw);

      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setSuccess("Kata sandi berhasil diperbarui.");

      setTimeout(() => {
        setShowChangePw(false);
        setSuccess("");
      }, 1200);
    } catch (e2) {
      const map = {
        "auth/wrong-password": "Kata Sandi saat ini salah.",
        "auth/invalid-credential": "Kata Sandi saat ini salah.",
        "auth/user-mismatch": "Tolong coba sign in kembali.",
        "auth/user-disabled": "Akun ini telah dinonaktifkan.",
        "auth/weak-password": "Kata sandi baru terlalu lemah.",
        "auth/too-many-requests": "Terlalu banyak percobaan. Mohon coba di sesaat lagi.",
        "auth/network-request-failed": "Terjadi masalah jaringan. Periksa koneksi Anda.",
      };
      setErr(map[e2?.code] || e2?.message || "Gagal perbarui kata sandi.");
    } finally {
      setSubmitting(false);
    }
  };

  const openChangePw = () => {
    setErr("");
    setSuccess("");
    setShowChangePw(true);
    setOpen(false);
    setOpenSheet(false);
  };

  return (
    <>
      <header
  className="bg-gradient-to-r from-white via-white to-blue-50/40 supports-[backdrop-filter]:backdrop-blur-md border-b border-white/70 sticky top-0 z-10 shadow-[0_2px_12px_rgba(2,6,23,0.06)]"

        style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}
      >
        {/* ===== Desktop / Tablet (unchanged) ===== */}
        <div className="hidden sm:flex max-w-7xl mx-auto items-center justify-between px-8 py-5">
          {/* Left */}
          <div className="flex items-center gap-4 min-w-0">
            {showBack && (
              <>
                <button
                  onClick={onBack}
                  className="inline-flex items-center justify-center rounded-xl border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition px-3 py-2"
                  aria-label="Kembali"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="ml-2">Kembali</span>
                </button>
                <div className="h-6 w-px bg-gray-200" />
              </>
            )}
            <img src={logo} alt="Potentia" className="h-9 w-auto shrink-0" />
            <div className="h-6 w-px bg-gray-300" />
            <span className="text-gray-700 font-semibold text-lg truncate">{title}</span>
          </div>

          {/* Right */}
          <div className="relative" ref={ddRef}>
            <button
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-3 hover:bg-gray-50 rounded-xl p-2 -m-2 transition-colors max-w-[60vw]"
              aria-expanded={open}
              aria-haspopup="menu"
            >
              <div className="text-right min-w-0">
                <div className="font-bold text-gray-900 text-base truncate">
                  {profile?.name || profile?.email || "—"}
                </div>
                {/*
				{resolvedSchool ? (
                  <div className="text-sm text-gray-500 font-medium truncate">{resolvedSchool}</div>
                ) : null}
				*/}
              </div>

              <div className="relative shrink-0">
                <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                  <span className="text-white font-bold text-base">{initials}</span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-sm" />
              </div>

              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
              <div
                className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200/50 py-2 z-50"
                role="menu"
              >
                <button
                  onClick={openChangePw}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  role="menuitem"
                >
                  <Key className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700 font-medium">Ganti kata sandi</span>
                </button>
                <hr className="border-gray-200 my-1" />
                <button
                  onClick={onLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  role="menuitem"
                >
                  <LogOut className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-700 font-medium">Keluar</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ===== Mobile (rearranged: two rows, full title) ===== */}
       {/* ===== Mobile (rearranged: two rows, full title) ===== */}
<div className="sm:hidden px-4 pt-2 pb-3">
  {/* Row 1: back + logo | avatar */}
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      {showBack && (
        <button
          onClick={onBack}
          className="inline-flex items-center justify-center rounded-lg border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition p-2"
          aria-label="Kembali"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      )}
      <img src={logo} alt="Potentia" className="h-8 w-auto drop-shadow-sm" />
    </div>

    <button
      onClick={() => setOpenSheet(true)}
      className="relative shrink-0 rounded-full ring-2 ring-white/80 shadow-md hover:scale-[1.02] active:scale-95 transition"

      aria-label="Account menu"
    >
      <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center border-2 border-white">
        <span className="text-white font-bold text-sm">{initials}</span>
      </div>
      <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white shadow-sm" />
    </button>
  </div>

  {/* Row 2: centered, wraps nicely */}
  <div className="mt-3 text-center">
    <h1 className="text-[18px] leading-snug font-semibold text-slate-900">
      {title}
    </h1>
    {resolvedSchool ? (
      <div className="text-[11px] text-slate-500 mt-0.5">
        {resolvedSchool}
      </div>
    ) : null}

    {/* soft divider under the header */}
    <div className="mx-auto mt-3 h-[6px] w-11/12 rounded-full bg-gradient-to-r from-transparent via-slate-200/80 to-transparent" />
  </div>
</div>

      </header>

      {/* Mobile bottom sheet for account menu */}
      {openSheet && (
        <div className="fixed inset-0 z-50 sm:hidden" aria-modal="true" role="dialog">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpenSheet(false)} />
          <div
            className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white shadow-2xl border-t border-gray-200"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}
          >
            <div className="px-4 pt-3 pb-2 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                  {initials}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate max-w-[70vw]">
                    {profile?.name || profile?.email || "—"}
                  </div>
                  {resolvedSchool ? (
                    <div className="text-xs text-gray-500 truncate max-w-[70vw]">{resolvedSchool}</div>
                  ) : null}
                </div>
              </div>
              <button
                onClick={() => setOpenSheet(false)}
                className="p-2 rounded-lg hover:bg-gray-100 active:scale-95 transition"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-2 py-1">
              <button
                onClick={openChangePw}
                className="w-full flex items-center gap-3 px-4 py-4 text-left active:scale-[0.99] transition rounded-xl hover:bg-gray-50"
              >
                <Key className="w-5 h-5 text-gray-600" />
                <div>
                  <div className="text-gray-800 font-medium">Ganti kata sandi</div>
                  <div className="text-xs text-gray-500">Perbarui sandi akun Anda</div>
                </div>
              </button>
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-3 px-4 py-4 text-left active:scale-[0.99] transition rounded-xl hover:bg-gray-50"
              >
                <LogOut className="w-5 h-5 text-gray-600" />
                <div>
                  <div className="text-gray-800 font-medium">Keluar</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Change Password (mobile-friendly) */}
      {showChangePw && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl sm:shadow-xl sm:max-h-[85vh] sm:overflow-hidden rounded-t-2xl"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
          >
            <div className="p-5 sm:p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <Key className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Ganti kata sandi</h3>
                  <p className="text-xs sm:text-sm text-gray-500">Perbarui kata sandi anda</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowChangePw(false);
                  setErr(""); setSuccess("");
                  setCurrentPw(""); setNewPw(""); setConfirmPw("");
                }}
                className="p-2 rounded-lg hover:bg-gray-100 active:scale-95 transition"
                aria-label="Tutup"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 sm:p-6 overflow-y-auto sm:max-h-[70vh]">
              {success && (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                  {success}
                </div>
              )}
              {err && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {err}
                </div>
              )}

              <form className="space-y-4" onSubmit={handleChangePassword} autoComplete="off">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kata Sandi Sekarang</label>
                  <div className="relative">
                    <input
                      type={showPw.current ? "text" : "password"}
                      name="current-password"
                      autoComplete="current-password"
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all pr-12"
                      placeholder="Masukkan Kata Sandi Saat Ini"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => ({ ...s, current: !s.current }))}
                      className="absolute inset-y-0 right-2 px-2 text-gray-500 hover:text-gray-700"
                      aria-label={showPw.current ? "Sembunyikan kata sandi" : "Tampilkan Kata Sandi"}
                    >
                      {showPw.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Kata Sandi Baru</label>
                  <div className="relative">
                    <input
                      type={showPw.next ? "text" : "password"}
                      name="new-password"
                      autoComplete="new-password"
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all pr-12"
                      placeholder="Masukkan Kata Sandi Baru"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => ({ ...s, next: !s.next }))}
                      className="absolute inset-y-0 right-2 px-2 text-gray-500 hover:text-gray-700"
                      aria-label={showPw.next ? "Sembunyikan Kata Sandi" : "Tampilkan Kata Sandi"}
                    >
                      {showPw.next ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Memastikan Kata Sandi Baru</label>
                  <div className="relative">
                    <input
                      type={showPw.confirm ? "text" : "password"}
                      name="confirm-new-password"
                      autoComplete="new-password"
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all pr-12"
                      placeholder="Memastikan Kata Sandi Baru"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => ({ ...s, confirm: !s.confirm }))}
                      className="absolute inset-y-0 right-2 px-2 text-gray-500 hover:text-gray-700"
                      aria-label={showPw.confirm ? "Sembunyikan Kata Sandi" : "Tampilkan Kata Sandi"}
                    >
                      {showPw.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 pt-2 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangePw(false);
                      setErr(""); setSuccess("");
                      setCurrentPw(""); setNewPw(""); setConfirmPw("");
                    }}
                    className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-3 text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-xl font-semibold transition-all shadow-lg disabled:opacity-50"
                  >
                    {submitting ? "Memperbarui…" : "Perbarui Kata Sandi"}
                  </button>
                </div>
              </form>

              <p className="mt-4 text-xs text-gray-500 text-justify">
                Catatan: Browser Anda mungkin menanyakan untuk memperbarui kata sandi yang tersimpan.
                Prompt ini hanya terkait penyimpanan lokal di perangkat Anda.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
